import { useContext, useCallback } from 'react';
import { useStore } from 'zustand';
import { ChatStepTimerContext } from './ChatStepTimerContext';
import { ChatStepTimerState, UNDEFINED_STORE } from './ChatStepTimerStore';
import { useTimer } from 'react-scale/components/scalers/withV2Timer';

/**
 * Hook for consumers of ChatStepTimerStore.
 * @example const submissionTimeMetric = useChatStepTimerStore(state => state?.submissionTimeMetric)
 * @param selector A callback where you can specify which part of the store you need. It's a common practice to slim down to a single element you need.
 * @returns The selected part of the store
 */
export const useChatStepTimerStore: <U>(
  selector: (state: ChatStepTimerState | undefined) => U,
) => U = selector => {
  const storeRef = useContext(ChatStepTimerContext);
  return useStore(storeRef?.current.store ?? UNDEFINED_STORE, selector);
};

/**
 * Hook for logging metrics. This should be called once when a step is submitted.
 *
 * @returns callback to log the submission time of a step
 */
export const useChatStepTimerLogStepSubmission = () => {
  const logStepSubmission = useChatStepTimerStore(state => state?.logStepSubmission);
  const { getTimerData } = useTimer();

  const logStepSubmissionCallback = useCallback(
    (stepId: string) => {
      logStepSubmission?.({ stepId, serverTimeSpent: getTimerData().serverTimeSpent });
    },
    [logStepSubmission, getTimerData],
  );

  return logStepSubmissionCallback;
};
